import { type ReactNode } from 'react';
import { Icon, type IconName } from './icon';
import styles from './empty-state.module.css';

const cx = (...names: Array<string | false | null | undefined>) =>
  names.filter(Boolean).join(' ');

type EmptyStateProps = {
  /** Icon rendered inside the soft accent badge above the headline. */
  icon: IconName;
  /** Headline text — already translated by the caller. */
  headline: ReactNode;
  /** Optional supporting line — already translated by the caller. */
  body?: ReactNode;
  /**
   * Optional action slot. Pass your own `<Button>` so call-site concerns stay
   * at the call site — an `onClick` handler (clear filters, retry) or an
   * `as={Link} href=…` navigation CTA (browse recipes).
   */
  action?: ReactNode;
  /** Heading level rendered for `headline` (default `h2`). */
  as?: 'h1' | 'h2' | 'h3';
  /** Optional id on the heading (e.g. to wire `aria-labelledby`). */
  id?: string;
  className?: string;
};

/**
 * EmptyState — the one shape every empty / not-found / filtered-empty surface
 * reuses: a soft accent icon badge, a display-type headline, a muted body line,
 * and an optional action slot.
 *
 * Presentational and Server-Component-safe: it never calls `useTranslations`.
 * Callers pass already-translated strings (resolved via `getTranslations` in a
 * server component or `useTranslations` in a client one). The heading level is
 * configurable via `as` so each surface keeps a correct document outline
 * (`h1` for a full-page error/not-found, `h2` for a section empty state).
 */
export function EmptyState({
  icon,
  headline,
  body,
  action,
  as: Heading = 'h2',
  id,
  className,
}: EmptyStateProps) {
  return (
    <div className={cx(styles.root, className)}>
      <span className={styles.icon}>
        <Icon name={icon} size={26} />
      </span>
      <Heading id={id} className={styles.headline}>
        {headline}
      </Heading>
      {body ? <p className={styles.body}>{body}</p> : null}
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
